import { memo, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { useTickCell, useTickSubscription } from '@/feed/useTick'
import { useSnapshot } from '@/feed/useSnapshot'
import { SPOT_TOKENS } from '@/feed/spotTokens'

/**
 * The index row's two halves: the tabs, and the one quote they select.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ONE QUOTE, NOT SIX TILES.
 *
 * Six tiles spent a full card row showing five prices nobody reads: a trader
 * works one chain at a time. The tabs name every index in a single segmented
 * control and the quote belongs to whichever is lit, which gives the ladder
 * back roughly 60px of height.
 *
 * THE TABS ARE STILL THE UNDERLYING SELECTOR. There is no separate row of
 * controls naming the same instruments twice.
 *
 * TWO EXPORTS, NOT ONE COMPONENT. The instrument search sits BETWEEN the tabs
 * and the quote in the top row, so the pair cannot be one element — and the
 * state that links them (which instrument is quoted) belongs to the Terminal,
 * beside the underlying it defaults to, rather than hidden in here where the
 * row's layout could not reach it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Kept as a named constant, not a live tab.
 *
 * India VIX is absent from the broker's instrument master, so there is no
 * token to subscribe and nothing to quote. `Terminal` still refers to this
 * when deciding whether a selection should load a chain; the moment the
 * master carries a VIX row, restoring the tab is adding it back to the list
 * the row renders.
 */
export const VIX_KEY = 'INDIAVIX'

const tabLabel = (key: string): string => (key === VIX_KEY ? 'INDIA VIX' : key)

export interface IndexTabsProps {
  /** The tradeable indices to offer as tabs. VIX is appended by the row. */
  underlyings: readonly string[]
  /** The underlying the CHAIN is on. */
  selected: string
  /** The instrument the quote beside the tabs is showing. */
  quoted: string
  /** A tradeable tab: load its chain. */
  onSelect: (key: string) => void
  /**
   * Quote-only selection — show a level without loading a chain.
   *
   * Nothing uses it while India VIX is absent from the instrument master,
   * and it is kept because the need returns the moment any quote-only
   * instrument does: routing one through `onSelect` would load an empty
   * ladder over the one being read.
   */
  onQuote?: (key: string) => void
}

export function IndexTabs({
  underlyings,
  selected,
  quoted,
  onSelect,
}: IndexTabsProps) {
  /*
   * THE TABS OWN THE SUBSCRIPTION.
   *
   * Every tab, not just the quoted one: switching tabs must show a price
   * immediately rather than a dash that fills in a second later, and three
   * index tokens against a 460-instrument chain is nothing. `selected` is in
   * the set too, because the Ctrl-K instrument search can put the chain on an
   * underlying that has no tab (FINNIFTY, BANKEX) and its quote still has to
   * render.
   *
   * The snapshot matters as much as the socket: outside market hours the
   * socket is asleep and the REST close is the only thing to show.
   */
  const tokens = useMemo(() => {
    const keys = new Set<string>([...underlyings, selected])
    return [...keys].map((k) => SPOT_TOKENS[k]).filter((t): t is number => t !== undefined)
  }, [underlyings, selected])
  useTickSubscription(tokens)
  useSnapshot(tokens)

  return (
    <div
      className="tr-ixtabs"
      role="tablist"
      aria-label="Index quotes — select one to load its option chain"
    >
      {/*
        TRADEABLE INDICES ONLY.
        India VIX used to sit here as a read-only quote tab. It is not in the
        broker's instrument master at all — see `spotTokens.ts` — so it had
        no token, no subscription and nothing to show, and a tab that can
        only ever render a dash reads as a broken feed rather than as an
        absent instrument.
      */}
      {underlyings.map((key) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={quoted === key}
          className={cn('tr-ixtab', quoted === key && 'on')}
          onClick={() => onSelect(key)}
          data-testid={`tile-${key}`}
        >
          {tabLabel(key)}
        </button>
      ))}
    </div>
  )
}

/**
 * The quote for whichever tab is lit: the price and the day's move.
 *
 * It holds NO hooks, so it can return early for an instrument with no token.
 * `useTickCell(0)` would track slot zero and hold it for the life of the tab
 * against a 1,024-slot store, for a token that can never tick.
 *
 * India VIX is NOT in the instrument catalogue (see `spotTokens.ts`), so it
 * says so rather than rendering a dash that reads as a feed fault.
 *
 * TODO: when VIX gains a catalogue entry, adding it to SPOT_TOKENS fills this
 * in — nothing here changes.
 */
export const IndexQuote = memo(function IndexQuote({ underlying }: { underlying: string }) {
  const token = SPOT_TOKENS[underlying]
  if (token === undefined) {
    return (
      <span className="tr-ixquote" data-testid="index-quote">
        <span className="tr-ixspot num text-muted-foreground">—</span>
        <span className="tr-chg text-muted-foreground">
          <span className="num">not subscribed</span>
        </span>
      </span>
    )
  }
  return <LiveQuote token={token} />
})

function LiveQuote({ token }: { token: number }) {
  /*
   * Two subscriptions, not one: `useTickCell` carries a single field and the
   * change needs the previous close beside the price. Two slots for one
   * readout — the one-subscription-per-cell discipline that matters is the
   * chain's 460, not this.
   */
  const { value: spot } = useTickCell(token, 'ltp')
  const { value: prev } = useTickCell(token, 'close')

  const hasSpot = spot > 0
  // The move against the previous close. Both have to be real: a change
  // computed from a missing close is a number that looks like information.
  const hasChange = hasSpot && prev > 0
  const chg = hasChange ? spot - prev : 0
  const pct = hasChange ? (chg / prev) * 100 : 0
  const up = chg >= 0

  return (
    <span className="tr-ixquote" data-testid="index-quote">
      <span className={cn('tr-ixspot num', !hasSpot && 'text-muted-foreground')}>
        {hasSpot
          ? spot.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : '—'}
      </span>
      <span className={cn('tr-chg', hasChange ? (up ? 'tr-up' : 'tr-dn') : 'text-muted-foreground')}>
        {hasChange ? (
          <>
            {/* Points first, then percent. The percent alone tells you how
                hard the index moved but not how far, and a strike ladder is
                read in points. */}
            <span className="num">{`${up ? '+' : ''}${chg.toFixed(2)}`}</span>
            <span className="num">{`${up ? '+' : ''}${pct.toFixed(2)}%`}</span>
          </>
        ) : (
          /* No previous close means no change. A dash, not a zero. */
          <span className="num">—</span>
        )}
      </span>
    </span>
  )
}
